import type { RoomSnapshot } from '../../lib/api/rooms'
import { getCardById } from '../../games/ideatthat/data/ingredientCards'
import { useT } from '../../i18n'

interface Props { snapshot: RoomSnapshot }

const COURSE_EMOJIS = ['🥗', '🍽️', '🍮']

/**
 * Presenting phase. One player at a time is the "presenter" — their
 * submission for the current course is displayed huge on the TV while
 * everyone else votes on their phones. The host cycles to the next
 * presenter from their phone; the TV just mirrors.
 */
export function TVPresentingPhase({ snapshot }: Props) {
  const t = useT()
  const { room, players, submissions, courseVotes } = snapshot

  // The server's presenter cursor indexes the NON-skipped seats only
  // (advance_phase counts `where not skipped`), so filter before
  // indexing — same as LiveRoomScreen. Indexing the raw list showed
  // the wrong presenter on the TV after any mid-game skip.
  const activePlayers = players.filter(p => !p.skipped)
  const presenter = activePlayers[room.current_player_index] ?? activePlayers[0]
  const presenterSub = presenter
    ? submissions.find(s => s.player_id === presenter.id) ?? null
    : null

  const courseIdx = (room.active_course - 1) as 0 | 1 | 2
  const courseName = courseIdx === 0 ? t('Starter') : courseIdx === 1 ? t('Main') : t('Dessert')
  const courseEmoji = COURSE_EMOJIS[courseIdx]

  const courseText = !presenterSub ? '' :
    courseIdx === 0 ? presenterSub.course1_text :
    courseIdx === 1 ? presenterSub.course2_text :
                      presenterSub.course3_text
  const courseIds = !presenterSub ? [] :
    courseIdx === 0 ? presenterSub.course1_ids :
    courseIdx === 1 ? presenterSub.course2_ids :
                      presenterSub.course3_ids

  // Tally votes for this (submission, course) pair.
  const votes = courseVotes.filter(cv =>
    cv.submission_id === presenterSub?.id && cv.course_number === room.active_course
  )
  const yesCount = votes.filter(v => v.would_eat).length
  const noCount  = votes.filter(v => !v.would_eat).length
  const totalVoters = Math.max(0, activePlayers.length - 1) // presenter doesn't vote
  const votedCount  = yesCount + noCount
  const everyoneVoted = totalVoters > 0 && votedCount >= totalVoters

  // How far through the course are we? (Counting active seats only.)
  const presenterNumber = room.current_player_index + 1

  const cards = courseIds.map(id => getCardById(id)).filter(Boolean)

  return (
    <div className="w-full max-w-6xl text-center">
      {/* Breadcrumb */}
      <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-3">
        {t('Course {n} of 3 · {emoji} {name} · Presenter {p} of {total}', {
          n: room.active_course,
          emoji: courseEmoji,
          name: courseName,
          p: presenterNumber,
          total: players.length,
        })}
      </p>

      {/* Who */}
      <h1 className="font-display text-7xl md:text-8xl font-bold text-brand-primary mb-2
                     leading-none animate-scale-in" key={presenter?.id ?? 'none'}>
        {presenter?.display_name ?? t('Someone')}
      </h1>
      <p className="text-2xl text-brand-muted mb-10">
        {courseIdx === 0
          ? t('presents their starter…')
          : courseIdx === 1
            ? t('presents their main…')
            : t('presents their dessert…')}
      </p>

      {/* The story */}
      <div className="card-lg p-10 md:p-14 mb-8 bg-white text-left min-h-[30vh]
                      flex items-center justify-center">
        {courseText && courseText.trim() ? (
          <p className="font-display text-4xl md:text-5xl font-medium text-brand-text
                        leading-snug">
            "{courseText}"
          </p>
        ) : (
          <p className="text-3xl text-brand-muted text-center w-full">
            {t('(No story was written for this course.)')}
          </p>
        )}
      </div>

      {/* Ingredients they used */}
      {cards.length > 0 && (
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-3">
            {t('Ingredients')}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {cards.map(c => c && (
              <span key={c.id}
                    className="px-4 py-2 rounded-full text-lg font-semibold"
                    style={{
                      backgroundColor: c.color,
                      color: contrastText(c.color),
                    }}>
                {c.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Live vote tally */}
      <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
        <div className="card-lg p-6 bg-iet-green/10 border-iet-green/30">
          <p className="text-xs font-semibold uppercase tracking-widest text-iet-green mb-1">{t("I'd eat that")}</p>
          <p className="font-display text-6xl font-bold text-iet-green">{yesCount}</p>
        </div>
        <div className="card-lg p-6 bg-brand-danger/10 border-brand-danger/30">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-danger mb-1">{t('Hard pass')}</p>
          <p className="font-display text-6xl font-bold text-brand-danger">{noCount}</p>
        </div>
      </div>

      <p className="text-xl text-brand-muted mb-6">
        {totalVoters === 0
          ? t('No one else to vote.')
          : everyoneVoted
            ? t('Everyone has voted!')
            : t('{n} of {total} voted', { n: votedCount, total: totalVoters })}
      </p>

      <p className="text-xl text-brand-muted">
        {everyoneVoted ? t('On to the next…') : t('Voting on their phones…')}
      </p>
    </div>
  )
}

function contrastText(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return lum > 0.55 ? '#1A1A1A' : '#FFFFFF'
}
